import React, { useContext, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import { Spinner } from 'react-bootstrap';
import { BoxSeam, PencilSquare } from 'react-bootstrap-icons';
import { AppContext } from '../../App';
import { requestQuoteEmailsFromProject } from '../../clients/project_client';
import { requestQuotesFromCatalog } from '../../clients/quote_client';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';
import { catalogEmailSuccessMessage } from '../../utils/catalogQuoteEmailResponse';
import {
  buildCatalogQuoteLinesFromProjectProducts,
  buildProjectQuoteEmailLinesFromProducts,
  hasQuotableAdHocProducts,
  hasQuotableInventoryProducts,
} from '../../utils/projectQuoteRequest';

const LOG_CONTEXT = 'ProjectProductsQuotePanel';

/**
 * Single labeled quote action (catalog inventory vs custom ad-hoc line).
 *
 * @param {Object} props
 * @param {string} props.label
 * @param {string} props.subtitle
 * @param {string} props.buttonLabel
 * @param {string} props.sendingLabel
 * @param {string} props.variant
 * @param {React.ComponentType} props.icon
 * @param {boolean} props.available
 * @param {boolean} props.showNoQuoteHint
 * @param {string} props.noQuoteHint
 * @param {boolean} props.disabled
 * @param {string} props.disabledTitle
 * @param {boolean} props.isSending
 * @param {function} props.onClick
 */
function QuoteActionBlock({
  label,
  subtitle,
  buttonLabel,
  sendingLabel,
  variant,
  icon: Icon,
  available,
  showNoQuoteHint,
  noQuoteHint,
  disabled,
  disabledTitle,
  isSending,
  onClick,
}) {
  if (!available && !showNoQuoteHint) {
    return null;
  }

  return (
    <div className="d-flex flex-column align-items-end text-end" style={{ minWidth: '12rem' }}>
      <span className="fw-semibold small text-uppercase text-muted">{label}</span>
      <span className="text-muted small mb-1">{subtitle}</span>
      {showNoQuoteHint ? (
        <span className="text-muted small fst-italic">{noQuoteHint}</span>
      ) : (
        <Button
          variant={variant}
          size="sm"
          disabled={disabled}
          title={disabledTitle || undefined}
          onClick={onClick}
          onMouseDown={e => e.stopPropagation()}
          className="d-inline-flex align-items-center"
        >
          {isSending ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              {sendingLabel}
            </>
          ) : (
            <>
              <Icon className="me-1" aria-hidden />
              {buttonLabel}
            </>
          )}
        </Button>
      )}
    </div>
  );
}

/**
 * Quote-request actions for one supplier group on Project detail (catalog vs custom).
 *
 * @param {Object} props
 * @param {string|number} props.projectId
 * @param {string} props.supplierName
 * @param {Array<Object>} props.inventoryProducts
 * @param {Array<Object>} props.adHocProducts
 * @param {boolean} props.hasGlobalContact
 * @param {string} [props.contactDisabledReason]
 * @param {function} [props.onQuotesSent]
 */
const ProjectProductsQuotePanel = ({
  projectId,
  supplierName,
  inventoryProducts = [],
  adHocProducts = [],
  hasGlobalContact,
  contactDisabledReason = '',
  onQuotesSent,
}) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const [isSendingInventory, setIsSendingInventory] = useState(false);
  const [isSendingAdHoc, setIsSendingAdHoc] = useState(false);
  const [errors, setErrors] = useState([]);

  const inventoryLines = useMemo(
    () => buildCatalogQuoteLinesFromProjectProducts(inventoryProducts),
    [inventoryProducts]
  );
  const adHocLines = useMemo(
    () => buildProjectQuoteEmailLinesFromProducts(adHocProducts),
    [adHocProducts]
  );

  const inventoryAvailable = hasQuotableInventoryProducts(inventoryProducts);
  const adHocAvailable = hasQuotableAdHocProducts(adHocProducts);
  const hasInventoryLines = inventoryProducts.length > 0;
  const hasAdHocLines = adHocProducts.length > 0;
  const showInventoryNoQuoteHint = hasInventoryLines && !inventoryAvailable;
  const showAdHocNoQuoteHint = hasAdHocLines && !adHocAvailable;

  const sendDisabledReason =
    contactDisabledReason ||
    (!hasGlobalContact
      ? 'Quote requests are disabled until a lab contact is configured.'
      : '');

  const catalogCountLabel =
    inventoryLines.length === 1
      ? '(1 catalog line)'
      : `(${inventoryLines.length} catalog lines)`;
  const customCountLabel =
    adHocLines.length === 1
      ? '(1 custom line)'
      : `(${adHocLines.length} custom lines)`;

  const handleInventoryQuotes = async e => {
    e?.stopPropagation?.();
    if (!token || !inventoryAvailable || sendDisabledReason) return;

    const numericProjectId = Number(projectId);
    setIsSendingInventory(true);
    setErrors([]);
    logInfo(LOG_CONTEXT, 'catalog quote submit', {
      projectId: numericProjectId,
      supplierName,
      lineCount: inventoryLines.length,
    });

    const response = await requestQuotesFromCatalog(token, {
      lines: inventoryLines,
      project_id: Number.isInteger(numericProjectId) ? numericProjectId : undefined,
    });
    setIsSendingInventory(false);

    if (response?.success) {
      showToast(
        catalogEmailSuccessMessage(response.data),
        'success',
        'top-right',
        5000
      );
      onQuotesSent?.();
      return;
    }

    setErrors(response?.messages?.length ? response.messages : ['Request failed.']);
  };

  const handleAdHocQuotes = async e => {
    e?.stopPropagation?.();
    if (!token || !adHocAvailable || sendDisabledReason) return;

    setIsSendingAdHoc(true);
    setErrors([]);
    logInfo(LOG_CONTEXT, 'custom line quote submit', {
      projectId,
      supplierName,
      lineCount: adHocLines.length,
    });

    const response = await requestQuoteEmailsFromProject(token, projectId, {
      lines: adHocLines,
      update_status: true,
    });
    setIsSendingAdHoc(false);

    if (response?.success) {
      showToast(
        catalogEmailSuccessMessage(response.data),
        'success',
        'top-right',
        5000
      );
      onQuotesSent?.();
      return;
    }

    setErrors(response?.messages?.length ? response.messages : ['Request failed.']);
  };

  if (
    isViewer ||
    (!inventoryAvailable &&
      !adHocAvailable &&
      !showInventoryNoQuoteHint &&
      !showAdHocNoQuoteHint)
  ) {
    return null;
  }

  const actionDisabled =
    !!sendDisabledReason || isSendingInventory || isSendingAdHoc;

  return (
    <div className="d-flex flex-column align-items-end gap-2">
      {errors.length > 0 ? (
        <Alert variant="danger" className="mb-0 py-2 px-3">
          {errors[0]}
        </Alert>
      ) : null}
      <div className="d-flex flex-column flex-md-row gap-3 align-items-stretch align-items-md-end">
        <QuoteActionBlock
          label="Catalog inventory"
          subtitle={
            inventoryAvailable
              ? `Sends email for products in Materiah inventory ${catalogCountLabel}`
              : 'Sends email for products in Materiah inventory'
          }
          buttonLabel="Request catalog quotes"
          sendingLabel="Sending catalog quotes…"
          variant="success"
          icon={BoxSeam}
          available={inventoryAvailable}
          showNoQuoteHint={showInventoryNoQuoteHint}
          noQuoteHint="No catalog lines need a quote"
          disabled={actionDisabled}
          disabledTitle={
            sendDisabledReason ||
            'Send quote requests for catalog products with status Needs Quote'
          }
          isSending={isSendingInventory}
          onClick={handleInventoryQuotes}
        />
        <QuoteActionBlock
          label="Custom lines"
          subtitle={
            adHocAvailable
              ? `Sends email for lines not in inventory ${customCountLabel}`
              : 'Sends email for lines not in inventory'
          }
          buttonLabel="Request custom line quotes"
          sendingLabel="Sending custom quotes…"
          variant="outline-warning"
          icon={PencilSquare}
          available={adHocAvailable}
          showNoQuoteHint={showAdHocNoQuoteHint}
          noQuoteHint="No custom lines ready to quote"
          disabled={actionDisabled}
          disabledTitle={
            sendDisabledReason ||
            'Send quote requests for custom lines with supplier and status Needs Quote'
          }
          isSending={isSendingAdHoc}
          onClick={handleAdHocQuotes}
        />
      </div>
    </div>
  );
};

export default ProjectProductsQuotePanel;
